import React, { PropsWithChildren, useState } from 'react';
import {
  faArrowDown,
  faArrowUp,
  faChevronDown,
  faChevronUp,
  faClone,
  faRightLeft,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Toggle } from '@client/components/common/inputs/Toggle';
import { useThenableModal } from '@client/components/common/Modal';
import { HideWarningModal } from '../Modals';
import { ColorPicker } from '../ColorPicker';
import { useFocusWithin } from 'react-aria';
import { AddSection } from './AddSection';
import { HeroSelectModal } from './HeroSelectModal';
import { Tooltip } from '@client/new/components/basic';
import classNames from 'classnames';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { ISection } from '../types';
import { Typography } from '@design-system/components/typography/typography';
import type { BuilderType } from '../section-config';

export const Container = ({
  children,
  title,
  alwaysEnabled,
  enabled,
  onChangeEnabled,
  onMove,
  colors,
  color,
  onColorIndexChange,
  colorSelectEnabled = true,
  canMoveUp = false,
  canMoveDown = false,
  onAddSection,
  onDeleteSection,
  onSwitchHeader,
  onDuplicateSection,
  sectionType,
  id,
  hasError = false,
  folded: controlledFolded,
  setFolded: controlledSetFolded,
  hasOfferId,
  builderType,
}: {
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  color: string;
  colors: string[];
  children: React.ReactNode;
  title: string;
  alwaysEnabled?: boolean;
  enabled?: boolean;
  onChangeEnabled?: (val: boolean) => void;
  onColorIndexChange?: (val: number) => void;
  colorSelectEnabled?: boolean;
  onMove?: (by: number) => void;
  onAddSection?: (s: string) => void;
  onDeleteSection?: () => void;
  onDuplicateSection?: () => void;
  onSwitchHeader?: (o: ISection['type']) => void;
  sectionType?: ISection['type'];
  id?: string;
  hasError?: boolean;
  folded?: boolean;
  setFolded?: (val: boolean) => void;
  hasOfferId?: boolean;
  builderType?: BuilderType;
}) => {
  const [internalFolded, setInternalFolded] = useState(!enabled);

  // Use controlled state if provided, otherwise use internal state
  const folded =
    controlledFolded !== undefined ? controlledFolded : internalFolded;
  const setFolded = controlledSetFolded || setInternalFolded;
  const hideWarningModal = useThenableModal(HideWarningModal);
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => {
      setFolded(false);
    },
  });

  const handleHideToggleChange = async (val: boolean) => {
    if (val) {
      setFolded(false);
      return onChangeEnabled && onChangeEnabled(true);
    }

    const choice = await hideWarningModal.open();
    if (choice) {
      setFolded(true);
      return onChangeEnabled && onChangeEnabled(false);
    }
  };

  const handleColorChange = (val: string) => {
    const index = colors.findIndex((c) => c === val);
    onColorIndexChange && onColorIndexChange(index);
  };

  return (
    <>
      <div id={id} className="bg-white mb-6 rounded-lg" {...focusWithinProps}>
        <div
          className={`
          w-full flex flex-wrap bg-[#E1E5F0] ${
            enabled ? '' : 'opacity-55'
          } items-center justify-between lg:h-[70px]
          ${folded ? 'rounded-lg' : 'rounded-t-lg'}
          cursor-pointer
        `}
          onClick={() => {
            // If controlled, call with inverted value
            if (controlledSetFolded) {
              controlledSetFolded(!folded);
            } else {
              // If uncontrolled, use updater function
              setInternalFolded((x) => !x);
            }
          }}
        >
          <div className="flex items-center w-full md:w-auto h-[70px] justify-between px-2.5">
            <Typography
              className="md:pl-[25px] text-ellipsis max-w-[150px] 2xl:max-w-none whitespace-nowrap"
              variant="title"
              fontWeight="medium"
              size="large"
              style={{ pointerEvents: 'none' }}
              {...(hasError ? { color: 'ERROR-100' } : {})}
            >
              {title}
            </Typography>

            <div className="md:hidden" onClick={(e) => e.stopPropagation()}>
              <ColorPicker
                disableHex
                disableLabels
                value={color || '#FFFFFF'}
                onChange={handleColorChange}
                error={false}
                colors={colors}
                borderColor="#6367EF"
              />
            </div>
          </div>
          <div
            className="hidden md:flex flex-wrap items-center h-full"
            onClick={(e) => e.stopPropagation()}
          >
            {colorSelectEnabled && (
              <>
                <div className="ml-8 mr-8 flex items-center">
                  <div className="hidden md:block mr-4">Background: </div>
                  <ColorPicker
                    disableHex
                    disableLabels
                    value={color || '#FFFFFF'}
                    onChange={handleColorChange}
                    error={false}
                    colors={colors}
                    borderColor="#6367EF"
                  />
                </div>
                <Separator />
              </>
            )}
            {alwaysEnabled ? null : (
              <>
                <Tooltip title={enabled ? 'Disable section' : 'Enable section'}>
                  <ActionBox onClick={() => handleHideToggleChange(!enabled)}>
                    <div className="pointer-events-none">
                      <Toggle
                        value={Boolean(enabled)}
                        onChange={handleHideToggleChange}
                      />
                    </div>
                  </ActionBox>
                </Tooltip>
                <Separator />
              </>
            )}
            {onDuplicateSection && (
              <>
                <Tooltip title="Duplicate">
                  <ActionBox onClick={onDuplicateSection}>
                    <IconWithoutTooltip icon={faClone} />
                  </ActionBox>
                </Tooltip>
                <Separator />
              </>
            )}
            {onDeleteSection && (
              <>
                <Tooltip title="Delete">
                  <ActionBox
                    onClick={() => {
                      if (
                        window.confirm(
                          'Do you really want to delete this section?',
                        )
                      ) {
                        onDeleteSection();
                      }
                    }}
                  >
                    <IconWithoutTooltip
                      icon={faTrash}
                      className="group-hover:text-red-500"
                    />
                  </ActionBox>
                </Tooltip>
                <Separator />
              </>
            )}
            {onSwitchHeader && (
              <>
                <Tooltip title="Change Hero">
                  <ActionBox>
                    <SwitchHeaderIcon onSwitchHeader={onSwitchHeader} />
                  </ActionBox>
                </Tooltip>
                <Separator />
              </>
            )}
            <Tooltip title="Move up">
              <ActionBox
                style={{
                  opacity: canMoveUp ? 1 : 0.2,
                  cursor: canMoveUp ? 'pointer' : 'default',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  canMoveUp && onMove && onMove(-1);
                }}
              >
                <IconWithoutTooltip icon={faArrowUp} />
              </ActionBox>
            </Tooltip>
            <Separator />
            <Tooltip title="Move down">
              <ActionBox
                style={{
                  opacity: canMoveDown ? 1 : 0.2,
                  cursor: canMoveDown ? 'pointer' : 'default',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  canMoveDown && onMove && onMove(1);
                }}
              >
                <IconWithoutTooltip icon={faArrowDown} />
              </ActionBox>
            </Tooltip>
            <Separator />
            <Tooltip title={folded ? 'Expand' : 'Collapse'}>
              <ActionBox
                onClick={(e) => {
                  e.stopPropagation();
                  if (controlledSetFolded) {
                    controlledSetFolded(!folded);
                  } else {
                    setInternalFolded((x) => !x);
                  }
                }}
              >
                <IconWithoutTooltip
                  icon={folded ? faChevronUp : faChevronDown}
                />
              </ActionBox>
            </Tooltip>
          </div>

          <div
            className="w-full flex justify-end"
            onClick={(e) => e.stopPropagation()}
          >
            <ToggleButtonGroup value={'left'} exclusive className="md:hidden">
              {onDeleteSection && (
                <ToggleButton
                  value="left"
                  onClick={() => {
                    if (
                      window.confirm(
                        'Do you really want to delete this section?',
                      )
                    ) {
                      onDeleteSection();
                    }
                  }}
                >
                  <Tooltip title="Delete">
                    <div>
                      <FontAwesomeIcon
                        icon={faTrash}
                        className="text-red-500 pointer-events-none"
                      />
                    </div>
                  </Tooltip>
                </ToggleButton>
              )}
              {alwaysEnabled ? null : (
                <ToggleButton
                  value="left"
                  onClick={() => handleHideToggleChange(!enabled)}
                >
                  <Tooltip
                    title={enabled ? 'Disable section' : 'Enable section'}
                  >
                    <div className="pointer-events-none">
                      <Toggle
                        value={Boolean(enabled)}
                        onChange={handleHideToggleChange}
                      />
                    </div>
                  </Tooltip>
                </ToggleButton>
              )}
              {onDuplicateSection && (
                <ToggleButton value="left" onClick={onDuplicateSection}>
                  <Tooltip title="Duplicate">
                    <div>
                      <FontAwesomeIcon
                        icon={faClone}
                        className="pointer-events-none"
                      />
                    </div>
                  </Tooltip>
                </ToggleButton>
              )}
              {onSwitchHeader && (
                <ToggleButton value="left">
                  <Tooltip title="Change Hero">
                    <SwitchHeaderIcon onSwitchHeader={onSwitchHeader} />
                  </Tooltip>
                </ToggleButton>
              )}
              <ToggleButton
                value="left"
                onClick={() => canMoveUp && onMove && onMove(-1)}
              >
                <Tooltip title="Move up">
                  <div>
                    <FontAwesomeIcon
                      icon={faArrowUp}
                      className="pointer-events-none"
                    />
                  </div>
                </Tooltip>
              </ToggleButton>
              <ToggleButton
                value="left"
                onClick={() => canMoveDown && onMove && onMove(1)}
              >
                <Tooltip title="Move down">
                  <div>
                    <FontAwesomeIcon
                      icon={faArrowDown}
                      className="pointer-events-none"
                    />
                  </div>
                </Tooltip>
              </ToggleButton>
              <ToggleButton
                value="left"
                onClick={() => {
                  if (controlledSetFolded) {
                    controlledSetFolded(!folded);
                  } else {
                    setInternalFolded((x) => !x);
                  }
                }}
              >
                <Tooltip title={folded ? 'Expand' : 'Collapse'}>
                  <div>
                    <FontAwesomeIcon
                      icon={folded ? faChevronUp : faChevronDown}
                      className="pointer-events-none"
                    />
                  </div>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </div>

        <div
          className={`${
            folded
              ? 'max-h-0 overflow-hidden'
              : 'block p-[30px] pt-[20px] bg-white ' +
                (enabled ? '' : 'opacity-50')
          }`}
          style={{
            borderBottomRightRadius: '12px',
            borderBottomLeftRadius: '12px',
          }}
        >
          {children}
        </div>
      </div>
      <hideWarningModal.Popup />
      {onAddSection && (
        <div className="flex w-full my-6">
          <div className="flex flex-grow items-center">
            <div className="border-t-[1px] border-primary flex-grow" />
          </div>
          <div className="w-10 flex justify-center mx-6">
            <AddSection
              onAdd={onAddSection}
              hasOfferId={hasOfferId}
              builderType={builderType}
            />
          </div>
          <div className="flex flex-grow items-center">
            <div className="border-t-[1px] border-primary flex-grow" />
          </div>
        </div>
      )}
    </>
  );
};

export const ActionBox = React.forwardRef<
  HTMLDivElement,
  PropsWithChildren<
    React.HTMLAttributes<HTMLDivElement> & { className?: string }
  >
>(({ children, className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      {...props}
      className={classNames(
        'group flex w-[60px] xl:w-[75px] h-full items-center justify-center px-2 xl:px-3 hover:bg-gray-100 transition-colors',
        className,
      )}
    >
      {children}
    </div>
  );
});
ActionBox.displayName = 'ActionBox';

const Separator = () => <div className="border-r border-white h-full" />;

// Icon without tooltip - uses group-hover for scale effect
export function IconWithoutTooltip({
  icon,
  className,
}: {
  icon: any;
  className?: string;
}) {
  return (
    <FontAwesomeIcon
      icon={icon}
      className={classNames(
        'cursor-pointer text-base md:text-lg xl:text-2xl group-hover:scale-125 transition-transform',
        className || 'group-hover:text-primary',
      )}
    />
  );
}

// Switch header icon without tooltip
function SwitchHeaderIcon({
  onSwitchHeader,
}: {
  onSwitchHeader: (o: string) => void;
}) {
  const [show, setShow] = React.useState(false);

  if (show) {
    return (
      <HeroSelectModal
        onClose={() => setShow(false)}
        onHeroSelect={onSwitchHeader}
      />
    );
  }
  return (
    <FontAwesomeIcon
      icon={faRightLeft}
      className="cursor-pointer text-base md:text-lg xl:text-2xl group-hover:scale-125 group-hover:text-primary transition-transform"
      onClick={() => setShow(true)}
    />
  );
}
